/**
 * Ver la hoja antes de bajarla.
 *
 * El botón de PDF descargaba a ciegas: la única forma de saber qué traía el
 * archivo era abrirlo. Con rangos grandes eso son varios minutos por intento, y
 * el error típico —faltaba una columna, el periodo estaba mal— sólo se
 * descubría al final.
 */

import { useEffect, useState } from "react";

import Modal from "../../../components/base/Modal";
import { Button } from "../../../components/ui";
import VistaDocumento from "./VistaDocumento";
import { descargarPdf } from "./pdf";
import type { DocumentoReporte } from "./tipos";

export interface ModalVistaPreviaProps {
  documento: DocumentoReporte;
  generado: string;
  onClose: () => void;
  /** La descarga de Excel de la sección, para no duplicar su lógica aquí. */
  onExcel?: () => void | Promise<void>;
}

export default function ModalVistaPrevia({
  documento,
  generado,
  onClose,
  onExcel,
}: ModalVistaPreviaProps) {
  const [bajando, setBajando] = useState<"pdf" | "excel" | null>(null);

  /*
    ── Escape cierra ─────────────────────────────────────────────────────────
    `Modal` no lo trae, y aquí hace falta mas que en otros: la hoja es alta, se
    baja con la rueda, y si la unica salida es una X que quedo arriba, la
    pantalla se siente trabada. Escape es la salida que la gente ya intenta.
  */
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
  }, [onClose]);

  const pdf = async () => {
    setBajando("pdf");
    // Un tick para que el botón pinte su spinner antes del trabajo pesado.
    await new Promise((r) => setTimeout(r, 50));
    try {
      await descargarPdf(documento, generado);
    } finally {
      setBajando(null);
    }
  };

  const excel = async () => {
    if (!onExcel) return;
    setBajando("excel");
    try {
      await onExcel();
    } finally {
      setBajando(null);
    }
  };

  return (
    <Modal
      title="Vista previa del reporte"
      onClose={onClose}
      width="max-w-5xl"
      buttonAction={
        <div className="flex items-center gap-2">
          {/*
            `icon` de `Button` es un ReactNode, no una clase.

            Pasándole la cadena «ri-download-2-line» el botón la imprimía tal
            cual al lado del texto: en pantalla se leía
            «ri-download-2-lineDescargar PDF». El icono va envuelto en su `<i>`,
            y el giro de «generando» lo pone `loading`, que es para lo que está.
          */}
          {onExcel && (
            <Button
              size="sm"
              variant="successSoft"
              icon={<i className="ri-file-excel-2-line" aria-hidden="true" />}
              loading={bajando === "excel"}
              disabled={bajando !== null}
              onClick={excel}
            >
              Excel
            </Button>
          )}
          <Button
            size="sm"
            variant="primary"
            icon={<i className="ri-download-2-line" aria-hidden="true" />}
            loading={bajando === "pdf"}
            disabled={bajando !== null}
            onClick={pdf}
          >
            {bajando === "pdf" ? "Generando…" : "Descargar PDF"}
          </Button>
        </div>
      }
      content={
        /*
          ── La hoja scrollea AQUI, no el modal ───────────────────────────────
          `Modal` no le pone altura a su cuerpo, asi que un documento largo
          estiraba el dialogo hasta su tope y empujaba el encabezado fuera de
          la vista: al bajar a leer el detalle desaparecian la X y el boton de
          descargar, y no habia como salir ni como bajar el archivo.

          Con la altura acotada aqui, el dialogo nunca crece: el encabezado se
          queda quieto y lo que se desplaza es la hoja.
        */
        <div className="-mx-4 max-h-[70vh] overflow-auto overscroll-contain bg-subtle px-4 py-5 sm:-mx-6 sm:px-6">
          <VistaDocumento documento={documento} generado={generado} />
        </div>
      }
    />
  );
}
